import React, { useRef, useState, useEffect } from 'react';
import ConnectUs from './ConnectUs';
import { otpVerify, resendOtp } from '../Utils/api';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { updateCustomerDetails } from '../CustomerJourneyDetails/CustomerJourneyDetails';
import { useNavigate } from 'react-router-dom';

export default function OTPScreen() {
  const cname = process.env.REACT_APP_COMPANY_N;
  const [digits, setDigits] = useState(new Array(4).fill(''));
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const inputsRef = useRef([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const customerDetails = useSelector((state) => state.customerJourneyDetails.customerDetails);
  const click_Id = useSelector((state) => state.customerJourneyDetails.click_id);
  const utm_source = customerDetails?.utm_source;

  const hideNo = customerDetails?.mobile
    ? '*'.repeat(customerDetails.mobile.length - 4) + customerDetails.mobile.slice(-4)
    : '';

  /* ── Timer ── */
  useEffect(() => {
    if (timeLeft === 0) { setCanResend(true); return; }
    const t = setInterval(() => setTimeLeft((p) => p - 1), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  const startTimer = () => { setTimeLeft(30); setCanResend(false); };

  /* ── OTP input handling ── */
  const handleChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length > 1) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    setError('');

    if (value && index < 3) inputsRef.current[index + 1].focus();
    if (newDigits.every((d) => d !== '')) handleOtp(newDigits.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Enter' && !isVerifying) { e.preventDefault(); handleOtp(); }
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  

  /* ── Verify OTP ── */
  const handleOtp = async (otpValueParam = null) => {
    if (isVerifying) return;
    const otpValue = otpValueParam || digits.join('');
    if (otpValue.length < 4) { setError('Please enter all 4 digits of the OTP.'); return; }

    setIsVerifying(true);
    const param = {
      profileId: customerDetails?.profileId,
      mobile: customerDetails?.mobile,
      otp: otpValue,
    };

    try {
      const response = await otpVerify(param);
      if (response?.data?.apiStatus === 1) {
       
        if (utm_source == 'Valueleaf') {
     
          const url = `https://pixel.whistleloop.com/pixel?click_id=${click_Id}&revenue='revenue'&goal_name=OTP_Verified`;
          fetch(url).catch(() => {});
          
        }
        const formatPhone = (m) => m?.startsWith('+') ? m : `+91${m}`;

        

        toast.success('OTP verified successfully');
        setError('');
        dispatch(updateCustomerDetails({ token: response?.data?.data?.token }));

        

       

        if (response?.data?.data?.journeyCompletedFlag === 1) {
          dispatch(updateCustomerDetails({ journeyCompletedFlag: 1 }));
          window.location.href = '/journey/dashboard';
        } else {
          navigate('/journey');
        }
      } else {
        setError('The OTP you entered is incorrect. Please try again.');
      }
    } catch (err) {
      console.error('OTP Verification Failed', err);
      setError('Something went wrong during verification. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  /* ── Resend OTP ── */
  const handleResendOtp = async () => {
    try {
      const response = await resendOtp({ mobile: customerDetails?.mobile });
      if (response?.data?.apiStatus === 1) toast.success(response?.data?.message);
      else toast.error(response?.data?.message || 'Failed to resend OTP');
    } catch (err) {
      console.error(err);
      toast.error('Error resending OTP');
    } finally {
      startTimer();
    }
  };

  return (
    <div style={s.card}>

      {/* Brand bar */}
      <div style={s.brandBar}>
        <div style={s.brandDot} />
        <span style={s.brandLabel}>OTP Verification</span>
      </div>

      <p style={s.cardTitle}>Verify your number</p>
      <p style={s.cardSub}>We've sent a 4-digit OTP to your registered mobile number.</p>

      {/* Mobile number row */}
      <div style={s.mobRow}>
        <span style={s.mobNum}>+91 {hideNo}</span>
        <button style={s.editBtn} onClick={() => window.location.reload()}>
          ✏️ Change
        </button>
      </div>

      {/* OTP inputs */}
      <p style={s.otpLabel}>Enter 4-digit OTP</p>
      <div style={s.digitsRow}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength="1"
            value={digit}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            style={{
              ...s.digitInput,
              ...(error ? s.digitInputErr : {}),
              ...(digit && !error ? s.digitInputFilled : {}),
            }}
          />
        ))}
      </div>

      {error && (
        <p style={s.errorMsg}>⚠ {error}</p>
      )}

      {/* Verify button */}
      <button
        style={{ ...s.verifyBtn, ...(isVerifying ? s.verifyBtnDisabled : {}) }}
        onClick={() => handleOtp(digits.join(''))}
        disabled={isVerifying}
        onMouseEnter={(e) => { if (!isVerifying) e.currentTarget.style.background = '#ea6c0a'; }}
        onMouseLeave={(e) => { if (!isVerifying) e.currentTarget.style.background = '#F97316'; }}
      >
        {isVerifying ? 'Verifying…' : 'Verify OTP'}
      </button>

      <hr style={s.divider} />

      {/* Timer / Resend */}
      {!canResend ? (
        <div style={s.timerRow}>
          <span style={s.timerText}>Didn't receive the OTP?</span>
          <div style={s.timerBadge}>
            🕐 Resend in <span style={s.timerCount}>&nbsp;{timeLeft}s</span>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: '1rem' }}>
          <button
            style={s.resendBtn}
            onClick={handleResendOtp}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fff4ee'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            🔄 Resend OTP
          </button>
        </div>
      )}

      {/* Warning */}
      <div style={s.warnRow}>
        <span style={{ fontSize: '16px' }}>⚠️</span>
        <span>Never share your OTP with anyone. We will never ask for it.</span>
      </div>

  
      
    </div>
  );
}

const s = {
  card: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '2rem 2rem 1.75rem',
    width: '100%',
    maxWidth: '470px',
    border: '0.5px solid rgba(37,99,235,0.12)',
    boxShadow: '0 4px 32px rgba(37,99,235,0.07)',
    fontFamily: 'inherit',
  },
  brandBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '1.75rem',
  },
  brandDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#F97316',
    flexShrink: 0,
  },
  brandLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#64748b',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1e3a5f',
    marginBottom: '4px',
  },
  cardSub: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '1.5rem',
    lineHeight: 1.5,
  },
  mobRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'rgb(231,240,253)',
    borderRadius: '10px',
    padding: '10px 14px',
    marginBottom: '1.5rem',
  },
  mobNum: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e3a5f',
    letterSpacing: '1px',
  },
  editBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#F97316',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: 'inherit',
    padding: 0,
  },
  otpLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#64748b',
    marginBottom: '8px',
  },
  digitsRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '6px',
  },
  digitInput: {
    width: '60px',
    height: '64px',
    border: '1.5px solid #bfdbfe',
    borderRadius: '12px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e3a5f',
    background: '#f0f7ff',
    outline: 'none',
    transition: 'border-color 0.15s, background 0.15s',
    fontFamily: 'inherit',
  },
  digitInputFilled: {
    borderColor: '#F97316',
    background: '#fff4ee',
  },
  digitInputErr: {
    borderColor: '#dc2626',
    background: '#fff5f5',
  },
  errorMsg: {
    fontSize: '13px',
    color: '#dc2626',
    marginBottom: '10px',
    marginTop: '2px',
  },
  verifyBtn: {
    width: '100%',
    background: '#F97316',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    height: '50px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.18s',
    marginBottom: '1.25rem',
    letterSpacing: '0.2px',
  },
  verifyBtnDisabled: {
    background: '#fbb98a',
    cursor: 'not-allowed',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #dbeafe',
    marginBottom: '1.1rem',
  },
  timerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  timerText: {
    fontSize: '13px',
    color: '#64748b',
  },
  timerBadge: {
    background: 'rgb(231,240,253)',
    borderRadius: '20px',
    padding: '3px 12px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#1e3a5f',
    display: 'inline-flex',
    alignItems: 'center',
  },
  timerCount: {
    color: '#F97316',
    fontWeight: '700',
  },
  resendBtn: {
    background: 'transparent',
    border: '1.5px solid #F97316',
    color: '#F97316',
    borderRadius: '8px',
    padding: '7px 18px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.15s',
  },
  warnRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '12px',
    color: '#92400e',
    background: '#fffbeb',
    borderRadius: '8px',
    padding: '9px 12px',
    marginBottom: '1.25rem',
    border: '0.5px solid #fcd34d',
    lineHeight: 1.5,
  },
  connectSection: {
    borderTop: '1px solid #dbeafe',
    paddingTop: '1rem',
  },
  connectLabel: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '8px',
    fontWeight: '500',
  },
};